import {
  Button,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
  Box,
  AlertTypes,
  setAlert,
  MuiAlert,
  AlertTitle,
  Mui,
  MuiIcons,
} from "@leap4/frontend";
import dayjs from "dayjs";
import React, { useCallback, useContext, useMemo, useRef, useState } from "react";

import { useAppDispatch } from "../../../../store/hooks";
import { RunTimer } from "./runTimer";
import { convertArrayToMap } from "../../../../utils/functions";
import { ReportContext } from "../reportContext";
import {
  ADVERTISER_EVENT_METRICS,
  calcFamilyMatchesReportType,
  eventMetricKey,
  getDateDimensionLabel,
  getFilterDimensionOptionsForMetrics,
  getDimensionOptionsForMetrics,
  getTimeFromAggregationPresets,
  inferCalcReportFamily,
  isEventMetricKey,
  isPredictedMetricKey,
  PREDICTION_ANCHOR_METRIC,
  predictedMetricKey,
  eventNameFromPredictedMetricKey,
  PIXEL_DATE_METRICS,
  estimateReportSpeed,
  fastDimensionKeys,
  rawPixelOnlyRangeError,
  timeOptions,
  timePresets,
} from "../reports";
import Constraints from "./constraints";
import Filters from "./filters";
import ReportChart from "../chart/reportChart";
import { usePredictionSeries } from "../chart/usePredictionSeries";
import { ReportTable } from "./reportTable";
import { metricColumnName } from "../grid/reportColumns";
import { ChipPicker, ConfirmDialog, ILockedChip, IPickerItem, MetricSelect, Section, SpeedPill } from "./builderParts";

const { Paper, Skeleton, Link, Chip, Checkbox, FormControlLabel, Radio, RadioGroup, Tooltip: MuiTooltip } = Mui;

const REPORT_TYPES = [
  { value: "pixel", label: "Impression date", tip: "Rows by impression date; conversions credited to the impression." },
  { value: "advertiser", label: "Conversion date", tip: "Rows by the advertiser's conversion date." },
];

const FieldLabel = ({ children, sx }: { children: React.ReactNode; sx?: object }) => (
  <Typography variant="body2" component="div" sx={{ fontWeight: 500, mb: 0.75, ...sx }}>
    {children}
  </Typography>
);

const CAMPAIGN_DIMENSION = "id_campaign";

const createRowId = (prefix: string): string =>
  globalThis.crypto && typeof globalThis.crypto.randomUUID === "function"
    ? globalThis.crypto.randomUUID()
    : `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const createFilterId = (): string => createRowId("filter");
const createConstraintId = (): string => createRowId("constraint");

interface IMetricOption {
  key: string;
  label: string;
  hint?: string;
}

const ReportBuilderView = () => {
  const {
    report,
    setReport,
    columns,
    reportData,
    chartRows,
    chartLoading,
    runReport,
    cancelReport,
    isFetchingReportData,
    reportError,
    metadata,
    validationErrors,
    campaignOptions,
    campaignEventOptions,
    campaignCalculatedMetrics,
    setTabIndex,
  } = useContext(ReportContext);

  // Live, not on submit: the range and the metric can be chosen in either order, and either one
  // is what makes the pair invalid.
  const rangeError = useMemo(
    () =>
      rawPixelOnlyRangeError(
        report.metrics,
        report.startDate,
        report.endDate
      ),
    [report.metrics, report.startDate, report.endDate]
  );

  // Taken from the selected metrics in selection order, not from column order: the series is fetched
  // for one event and the chart must draw that same event's column, or the two disagree and the
  // forecast silently disappears.
  const predictedEventsInResult = useMemo(
    () =>
      Array.from(
        new Set(
          report.metrics
            .map((m) => m.eventType)
            .filter(isPredictedMetricKey)
            .map(eventNameFromPredictedMetricKey)
            .filter(Boolean)
        )
      ),
    [report.metrics]
  );
  // Every selected predicted metric is fetched and drawn. Each series carries its own event, so the
  // chart no longer has to guess which one a column belongs to.
  const { series: predictionSeries, error: predictionError } =
    usePredictionSeries(report.idCampaign, predictedEventsInResult);

  const [showForecast, setShowForecast] = useState(true);
  const forecastEnd = useMemo(
    () => predictionSeries.flatMap((s) => s.points.filter((p) => p.basis === "forecast").map((p) => String(p.pixelDate).slice(0, 10))).sort().pop() ?? null,
    [predictionSeries]
  );
  const todayYmd = dayjs().format("YYYY-MM-DD");
  const rangeHasToday =
    !!report.startDate && !!report.endDate && dayjs(report.startDate).format("YYYY-MM-DD") <= todayYmd && dayjs(report.endDate).format("YYYY-MM-DD") >= todayYmd;
  const forecastOn = showForecast && rangeHasToday;
  const forecastTip = !rangeHasToday
    ? "Include today in the range to forecast"
    : forecastEnd
    ? `Forecast for the ${dayjs(forecastEnd).diff(dayjs().startOf("day"), "day")} days beyond today, to ${dayjs(forecastEnd).format("MMM D")}`
    : "Forecast beyond today";

  const dispatch = useAppDispatch();

  const overrideLabelMap = useMemo(
    () =>
      new Map(
        campaignCalculatedMetrics
          .filter((c) => c.target_mode === "override" && c.target_column)
          .map((c) => [c.target_column as string, c.display_label])
      ),
    [campaignCalculatedMetrics]
  );

  const metricsOptionsForComponent = useMemo<IMetricOption[]>(() => {
    const family =
      report.reportType === "advertiser" ? "conversion" : "impression";
    const baseMetrics = metadata.metrics
      .filter(
        (m) => m.selectable !== false && (m.reports ?? []).includes(family)
      )
      .map((m) => ({
        key: m.key,
        label: overrideLabelMap.get(m.key) || m.label,
        hint: m.hint,
      }));
    const eventMetrics = campaignEventOptions

      .filter(
        (event) => report.reportType === "advertiser" || event.isAttributed
      )
      .map((event) => ({
        key: eventMetricKey(event.key),
        label:
          overrideLabelMap.get(eventMetricKey(event.key)) ||
          (report.reportType === "advertiser"
            ? event.value
            : `${event.value} (Attributed)`),
        hint:
          report.reportType === "advertiser"
            ? "raw advertiser events"
            : "attributed amount",
      }));
    // Events opted into prediction, and never on the advertiser family: a prediction is per
    // impression date, and the advertiser report has no impression date to hang one on.
    const predictedMetrics =
      report.reportType === "advertiser"
        ? []
        : campaignEventOptions
            .filter((event) => event.isPredicted)
            .map((event) => ({
              key: predictedMetricKey(event.key),
              label: `Predicted ${event.value}`,
              hint: "expected final total once the window closes",
            }));
    const calcMetricOptions = campaignCalculatedMetrics
      .filter((c) => c.target_mode === "new")
      .filter((c) =>
        calcFamilyMatchesReportType(
          inferCalcReportFamily(c.formula),
          report.reportType
        )
      )
      .sort(
        (a, b) =>
          a.display_order - b.display_order || a.key.localeCompare(b.key)
      )
      .map((c) => ({
        key: c.key,
        label: c.display_label,
        hint: "calculated column",
      }));
    return [
      ...baseMetrics,
      ...eventMetrics,
      ...predictedMetrics,
      ...calcMetricOptions,
    ];
  }, [
    campaignCalculatedMetrics,
    campaignEventOptions,
    report.reportType,
    overrideLabelMap,
    metadata.metrics,
  ]);

  const idsToMetrics = useMemo(
    () =>
      new Map(metricsOptionsForComponent.map((item) => [item.key, item.label])),
    [metricsOptionsForComponent]
  );
  /**
   * Shown but not chosen. A report of nothing but predictions asks the query for no columns, so the
   * request anchors itself to served impressions — this is that anchor made visible, greyed and
   * without a remove handle. Kept out of report.metrics deliberately: letting it look chosen would
   * let it be un-chosen, into a query with nothing to select.
   */
  const anchorMetricOption = useMemo(() => {
    const metrics = report.metrics ?? [];
    if (!metrics.some((m) => isPredictedMetricKey(m.eventType))) return null;
    if (metrics.some((m) => !isPredictedMetricKey(m.eventType))) return null;
    return (
      metricsOptionsForComponent.find(
        (o) => o.key === PREDICTION_ANCHOR_METRIC
      ) ?? null
    );
  }, [report.metrics, metricsOptionsForComponent]);

  const familyAnchorMetric =
    report.reportType === "advertiser"
      ? "advertiser_event_count"
      : "matched_impression_count";
  const withFamilyAnchor = useCallback(
    (metrics: Array<{ eventType: string; label: string }>) => {
      const hasCoreFamilyMetric = metrics.some((m) =>
        report.reportType === "advertiser"
          ? ADVERTISER_EVENT_METRICS.has(m.eventType)
          : PIXEL_DATE_METRICS.has(m.eventType)
      );
      if (hasCoreFamilyMetric || metrics.length === 0) {
        return metrics.length > 0
          ? metrics
          : [{ eventType: familyAnchorMetric, label: familyAnchorMetric }];
      }
      return [
        ...metrics,
        { eventType: familyAnchorMetric, label: familyAnchorMetric },
      ];
    },
    [familyAnchorMetric, report.reportType]
  );

  const metricsForDimensionFamily = useMemo(
    () => withFamilyAnchor(report.metrics),
    [report.metrics, withFamilyAnchor]
  );

  const customLabelMap = useMemo(
    () =>
      new Map(
        (report.customLabels || []).map((item) => [item.key, item.value])
      ),
    [report.customLabels]
  );

  const dimensionOptions = useMemo(
    () =>
      getDimensionOptionsForMetrics(
        metricsForDimensionFamily,
        metadata.dimensions
      ).map((option) => ({
        ...option,
        value:
          overrideLabelMap.get(option.key) ||
          customLabelMap.get(option.key) ||
          option.value,
      })),
    [
      customLabelMap,
      overrideLabelMap,
      metricsForDimensionFamily,
      metadata.dimensions,
    ]
  );
  const idsToDimensions = useMemo(
    () => convertArrayToMap(dimensionOptions),
    [dimensionOptions]
  );
  const filterDimensionOptions = useMemo(
    () =>
      getFilterDimensionOptionsForMetrics(
        metricsForDimensionFamily,
        dimensionOptions,
        metadata.dimensions
      ),
    [dimensionOptions, metricsForDimensionFamily, metadata.dimensions]
  );

  // Only metrics the report actually returns can be constrained; anything else is a 400.
  // Keys come from metricColumnName so they match the response columns exactly.
  // Predicted metrics are deliberately absent. They are merged into the rows after the query — after
  // grouping, after paging — so there is no SQL expression to compare against, and the backend
  // refuses a constraint it cannot bind to the same expression that produced the number.
  const constraintMetricOptions = useMemo(
    () =>
      report.metrics
        .filter((m) => !isPredictedMetricKey(m.eventType))
        .map((m) => ({ key: metricColumnName(m.eventType), value: m.label })),
    [report.metrics]
  );

  const dateDimensionLabel = getDateDimensionLabel(
    report.metrics,
    report.reportType
  );

  const speed = useMemo(
    () => estimateReportSpeed(report, metadata.grains, metadata.metrics),
    [report, metadata.grains, metadata.metrics]
  );

  const handleAggregationTimeChange = (event: any) => {
    setReport({ ...report, timeAggregation: event.target.value });
  };

  const handleAggregationPresetsChange = (event: any) => {
    const { startDate, endDate } = getTimeFromAggregationPresets(
      event.target.value,
      report.timezone
    );
    setReport({
      ...report,
      preset: event.target.value,
      startDate: startDate,
      endDate: endDate,
    });
  };

  const handleReportTypeChange = (event: any) => {
    const reportType = event.target.value as "pixel" | "advertiser";
    setReport({
      ...report,
      reportType,
      metrics: [],
      dimensions: [],
      filters: [],
    });
  };

  const getKeyValueArr = (
    chipsArray: string[],
    mapIdsToValue: Map<string, string>
  ) => {
    if (!chipsArray) return [];
    return chipsArray.map((chip) => ({
      key: chip.toString(),
      value: mapIdsToValue.get(chip.toString()) || chip,
    }));
  };

  const handleDimensionsChips = (chipsArray: string[]) => {
    const chips = getKeyValueArr(chipsArray, idsToDimensions);
    const validFilterDimKeys = new Set(
      getFilterDimensionOptionsForMetrics(
        report.metrics,
        dimensionOptions,
        metadata.dimensions
      ).map((o) => o.key)
    );
    const prunedFilters = report.filters.filter(
      (f) => !f.dimension || validFilterDimKeys.has(f.dimension)
    );
    setReport({ ...report, dimensions: chips, filters: prunedFilters });
  };

  const handleMetricsChips = (chipsArray: string[]) => {
    const uniqueChips = Array.from(new Set(chipsArray));
    // Derived from the catalogue, with the same filter the picker builds its options from. Hardcoded,
    // this list silently dropped any core metric the backend added: the option appeared, clicking it
    // did nothing, and nothing anywhere reported a reason.
    const family =
      report.reportType === "advertiser" ? "conversion" : "impression";
    const allowedCoreMetrics = new Set(
      metadata.metrics
        .filter(
          (m) => m.selectable !== false && (m.reports ?? []).includes(family)
        )
        .map((m) => m.key)
    );
    const calcKeys = new Set(
      campaignCalculatedMetrics
        .filter((c) => c.target_mode === "new")
        .map((c) => c.key)
    );
    const resolved = uniqueChips.filter(
      (eventType) =>
        isEventMetricKey(eventType) ||
        isPredictedMetricKey(eventType) ||
        allowedCoreMetrics.has(eventType) ||
        calcKeys.has(eventType)
    );

    const metricsToStore = resolved.map((eventType) => ({
      eventType,
      label: idsToMetrics.get(eventType) || eventType,
    }));

    const resolvedDimensionOptions = getDimensionOptionsForMetrics(
      withFamilyAnchor(metricsToStore),
      metadata.dimensions
    );
    const validDimKeys = new Set(resolvedDimensionOptions.map((o) => o.key));
    const validFilterDimKeys = new Set(
      getFilterDimensionOptionsForMetrics(
        metricsToStore,
        resolvedDimensionOptions,
        metadata.dimensions
      ).map((o) => o.key)
    );
    const prunedDimensions = report.dimensions.filter((d) =>
      validDimKeys.has(d.key)
    );
    const prunedFilterDimKeys = new Set(
      getFilterDimensionOptionsForMetrics(
        metricsToStore,
        resolvedDimensionOptions,
        metadata.dimensions
      ).map((o) => o.key)
    );
    const prunedFilters = report.filters.filter(
      (f) =>
        !f.dimension ||
        (validFilterDimKeys.has(f.dimension) &&
          prunedFilterDimKeys.has(f.dimension))
    );

    const removedKeys = Array.from(
      new Set([
        ...report.dimensions
          .filter((d) => !validDimKeys.has(d.key))
          .map((d) => d.key),
        ...report.filters
          .filter(
            (f) =>
              f.dimension &&
              !(
                validFilterDimKeys.has(f.dimension) &&
                prunedFilterDimKeys.has(f.dimension)
              )
          )
          .map((f) => f.dimension),
      ])
    );
    if (removedKeys.length > 0) {
      const removedLabels = removedKeys
        .map((key) => idsToDimensions.get(key) || key)
        .join(", ");
      dispatch(
        setAlert({
          msg: `Removed ${removedLabels} — not available for the selected metric(s).`,
          type: AlertTypes.WARNING,
          d: dispatch,
        })
      );
    }

    setReport({
      ...report,
      metrics: metricsToStore,
      dimensions: prunedDimensions,
      filters: prunedFilters,
    });
  };

  const dragSourceIndexRef = useRef<number | null>(null);

  const reorderItems = useCallback(
    <T,>(items: T[], fromIndex: number, toIndex: number): T[] => {
      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= items.length ||
        toIndex >= items.length
      ) {
        return items;
      }
      const next = [...items];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    },
    []
  );

  const reorderDimensions = useCallback(
    (fromIndex: number, toIndex: number) => {
      setReport({
        ...report,
        dimensions: reorderItems(report.dimensions, fromIndex, toIndex),
      });
    },
    [report, reorderItems, setReport]
  );

  const reorderMetrics = useCallback(
    (fromIndex: number, toIndex: number) => {
      setReport({
        ...report,
        metrics: reorderItems(report.metrics, fromIndex, toIndex),
      });
    },
    [report, reorderItems, setReport]
  );

  const getChipDragProps = useCallback(
    (
      index: number,
      onReorder: (fromIndex: number, toIndex: number) => void
    ) => ({
      draggable: true,

      onMouseDown: (e: React.MouseEvent) => {
        e.stopPropagation();
      },
      onDragStart: (e: React.DragEvent) => {
        dragSourceIndexRef.current = index;
        e.dataTransfer.effectAllowed = "move";

        e.dataTransfer.setData("text/plain", String(index));
      },
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const fromIndex = dragSourceIndexRef.current;
        if (fromIndex !== null && fromIndex !== index)
          onReorder(fromIndex, index);
        dragSourceIndexRef.current = null;
      },
      onDragEnd: () => {
        dragSourceIndexRef.current = null;
      },
      sx: { cursor: "grab", "&:active": { cursor: "grabbing" } },
    }),
    []
  );

  const handleFilterChange = (id: string, field: string, value: any) => {
    setReport({
      ...report,
      filters: report.filters.map((f) =>
        f.id === id ? { ...f, [field]: value } : f
      ),
    });
  };

  const handleAddFilter = () => {
    setReport({
      ...report,
      filters: [
        ...report.filters,
        {
          id: createFilterId(),
          dimension: "",
          action: "include",
          specTarget: [],
        },
      ],
    });
  };

  const handleDeleteFilter = (id: string) => {
    setReport({
      ...report,
      filters: report.filters.filter((f) => f.id !== id),
    });
  };

  const handleConstraintChange = (id: string, field: string, value: any) => {
    setReport({
      ...report,
      constraints: (report.constraints || []).map((c) =>
        c.id === id ? { ...c, [field]: value } : c
      ),
    });
  };

  const handleAddConstraint = () => {
    setReport({
      ...report,
      constraints: [
        ...(report.constraints || []),
        {
          id: createConstraintId(),
          metric: "",
          operator: "gt" as const,
          value: "" as const,
        },
      ],
    });
  };

  const handleDeleteConstraint = (id: string) => {
    setReport({
      ...report,
      constraints: (report.constraints || []).filter((c) => c.id !== id),
    });
  };


  const [pendingType, setPendingType] = useState<"pixel" | "advertiser" | null>(null);
  const [lastRunConfig, setLastRunConfig] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);

  // What a run depends on; template name and description changes don't make results stale.
  const runSignature = JSON.stringify({ ...report, _id: undefined, name: undefined, description: undefined, templateRevision: undefined });
  const settingsChanged = !!reportData && lastRunConfig !== null && lastRunConfig !== runSignature && !isFetchingReportData;

  const run = () => {
    setCancelled(false);
    setLastRunConfig(runSignature);
    runReport();
  };

  const cancel = () => {
    setCancelled(true);
    cancelReport();
  };

  const requestTypeChange = (next: "pixel" | "advertiser" | null) => {
    if (!next || next === report.reportType) return;
    const hasSelections = report.metrics.length > 0 || report.dimensions.length > 0 || report.filters.length > 0;
    if (hasSelections) setPendingType(next);
    else handleReportTypeChange({ target: { value: next } });
  };

  const isCustomRange = report.preset === "choose time preset";
  const toYmd = (value: string | null) => (value ? dayjs(value).format("YYYY-MM-DD") : "");
  const rangeDays = report.startDate && report.endDate ? dayjs(report.endDate).diff(dayjs(report.startDate), "day") + 1 : null;

  const calcKeySet = new Set(campaignCalculatedMetrics.map((c) => c.key));
  // Campaign scope: one, several, or every campaign ("all") the user can access.
  const selectedCampaigns = report.idCampaign === "all" ? campaignOptions : report.idCampaign.split(",").filter(Boolean);
  const onlyCampaign = campaignOptions.length === 1;
  const splitByCampaign = report.dimensions.some((d) => d.key === CAMPAIGN_DIMENSION);
  const setCampaigns = (next: string[]) => {
    const ordered = campaignOptions.filter((c) => next.includes(c));
    if (!ordered.length) return;
    const value = ordered.length > 1 && ordered.length === campaignOptions.length ? "all" : ordered.join(",");
    // Predictions and calculated metrics are fitted per campaign, so a multi-campaign report drops them.
    const metrics = ordered.length > 1 ? report.metrics.filter((m) => !isPredictedMetricKey(m.eventType) && !calcKeySet.has(m.eventType)) : report.metrics;
    setReport({ ...report, idCampaign: value, metrics });
  };
  const toggleSplit = () =>
    handleDimensionsChips(splitByCampaign ? report.dimensions.map((d) => d.key).filter((k) => k !== CAMPAIGN_DIMENSION) : [CAMPAIGN_DIMENSION, ...report.dimensions.map((d) => d.key)]);

  const fastKeys = new Set(fastDimensionKeys(report, metadata.grains));
  const categoryOf = new Map(metadata.dimensions.map((d) => [d.key, d.category || "Other"]));
  const userPickedDate = report.dimensions.some((d) => d.key === "pixel_date" || d.key === "event_date");
  const dimensionItems: IPickerItem[] = dimensionOptions.filter((o) => o.key !== CAMPAIGN_DIMENSION).map((o) => ({
    key: o.key,
    label: o.value,
    group: categoryOf.get(o.key) || "Other",
    fast: report.reportType === "pixel" && fastKeys.has(o.key),
  }));
  const dimensionSpeedNote =
    report.reportType === "advertiser" ? (
      undefined
    ) : fastKeys.size > 0 ? (
      <>
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", color: "success.main", fontWeight: 600 }}>
          <MuiIcons.Bolt aria-hidden="true" sx={{ fontSize: 14 }} />
          fast
        </Box>{" "}
        dimensions keep reports quick.
      </>
    ) : (
      "No fast dimensions with these metrics."
    );
  const lockedDimensions: ILockedChip[] =
    report.timeAggregation && report.timeAggregation !== "Sum" && !userPickedDate
      ? [{ key: "auto-date", label: `${dateDimensionLabel} (added by ${report.timeAggregation === "Day" ? "Day" : "Month"} aggregation)`, reason: "Switch to Sum to remove it." }]
      : [];

  const metricGroup = (key: string) =>
    isPredictedMetricKey(key) ? "Predicted" : isEventMetricKey(key) ? (report.reportType === "advertiser" ? "Events" : "Attributed events") : calcKeySet.has(key) ? "Calculated" : "Core";
  const metricItems: IPickerItem[] = metricsOptionsForComponent
    .filter((o) => o.key !== anchorMetricOption?.key || report.metrics.some((m) => m.eventType === o.key))
    .map((o) => ({
      key: o.key,
      label: o.label,
      group: metricGroup(o.key),
      hint: o.hint,
      tag: o.key === "reach" || o.key === "impression_frequency" ? "≤31 days" : undefined,
    }));
  const lockedMetrics: ILockedChip[] = anchorMetricOption
    ? [{ key: anchorMetricOption.key, label: anchorMetricOption.label, reason: "Added for the predictions. Pick another metric to remove it." }]
    : [];

  const speedReason = speed.level === "slow" ? "Please stay on this page until it finishes." : undefined;

  const presetLabel = isCustomRange
    ? rangeDays
      ? `${dayjs(report.startDate).format("MMM D")} – ${dayjs(report.endDate).format("MMM D, YYYY")}`
      : "No dates yet"
    : report.preset.replace("last calender", "Last calendar");
  const reportSummary = [
    selectedCampaigns.length === campaignOptions.length && campaignOptions.length > 1 ? "All campaigns" : selectedCampaigns.join(", "),
    presetLabel,
    report.timeAggregation,
    `${report.dimensions.length} dimension${report.dimensions.length === 1 ? "" : "s"}`,
    `${report.metrics.length} metric${report.metrics.length === 1 ? "" : "s"}`,
  ].join(" · ");

  const runDisabled = isFetchingReportData || !!rangeError || !metadata.loaded;
  const hasResults = !!reportData && reportData.length > 0;

  return (
    <Box>
      <Paper sx={{ border: 1, borderColor: "divider", boxShadow: "none", borderRadius: "12px", mb: 2.5 }}>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {`Date range applies to ${dateDimensionLabel}. `}Impression-date and conversion-date reports are separate modes; event metrics become columns, not rows.{" "}
          <Link component="button" type="button" onClick={() => setTabIndex(2)} sx={{ verticalAlign: "baseline", fontSize: "inherit" }}>
            How metrics work
          </Link>
        </Typography>

        {/* Crossroads' report form: setup on the left, the column choices on the right, every section collapsible. */}
        <Box sx={{ display: "grid", gap: { xs: 3, lg: 4 }, gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1fr)" }, alignItems: "start" }}>
          <Box component="aside" aria-label="Report setup" sx={{ display: "grid", gap: 3, alignContent: "start", minWidth: 0 }}>
              <Section title="Report settings">
                <FieldLabel>Report type</FieldLabel>
                <RadioGroup value={report.reportType} onChange={(_e: unknown, v: string) => requestTypeChange(v as "pixel" | "advertiser")} aria-label="Report type">
                  {REPORT_TYPES.map((t) => (
                    <FormControlLabel
                      key={t.value}
                      value={t.value}
                      control={<Radio size="small" />}
                      label={
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                          {t.label}
                          <MuiTooltip title={t.tip}>
                            <MuiIcons.InfoOutlined aria-label={t.tip} sx={{ fontSize: 16, color: "text.secondary" }} />
                          </MuiTooltip>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
                {campaignOptions.length > 1 && (
                  <Box sx={{ mt: 2 }}>
                    <FieldLabel>Campaigns</FieldLabel>
                    <Box role="group" aria-label="Campaigns" sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
                      {campaignOptions.map((c) => {
                        const on = selectedCampaigns.includes(c);
                        const last = on && selectedCampaigns.length === 1;
                        return (
                          <Chip
                            key={c}
                            size="small"
                            label={c}
                            color={on ? "primary" : "default"}
                            variant={on ? "filled" : "outlined"}
                            clickable={!onlyCampaign}
                            disabled={onlyCampaign}
                            aria-pressed={on}
                            title={onlyCampaign ? "The only campaign on your account" : last ? "A report needs at least one campaign" : undefined}
                            onClick={onlyCampaign || last ? undefined : () => setCampaigns(on ? selectedCampaigns.filter((x) => x !== c) : [...selectedCampaigns, c])}
                            sx={onlyCampaign ? { "&.Mui-disabled": { opacity: 1, bgcolor: "action.selected", color: "text.primary" } } : undefined}
                          />
                        );
                      })}
                      {!onlyCampaign && selectedCampaigns.length < campaignOptions.length && (
                        <Button size="small" onClick={() => setCampaigns(campaignOptions)} sx={{ px: 0.5 }}>
                          Select all
                        </Button>
                      )}
                    </Box>
                    {selectedCampaigns.length > 1 && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.25, flexWrap: "wrap" }}>
                        <Chip size="small" label="Split rows by campaign" color={splitByCampaign ? "primary" : "default"} variant={splitByCampaign ? "filled" : "outlined"} clickable aria-pressed={splitByCampaign} onClick={toggleSplit} />
                        <Typography variant="caption" color="text.secondary">
                          {splitByCampaign ? "Rows per campaign." : "Rows summed across campaigns."} Predicted and calculated metrics need one campaign.
                        </Typography>
                      </Box>
                    )}
                    {validationErrors.idCampaign && <Typography variant="caption" color="error" role="alert">{validationErrors.idCampaign}</Typography>}
                  </Box>
                )}
              </Section>

              <Section title="Time parameters" error={rangeError || undefined}>
                <FieldLabel>Time range</FieldLabel>
                <Select size="small" fullWidth value={report.preset} onChange={handleAggregationPresetsChange} inputProps={{ "aria-label": "Time range" }} sx={{ maxWidth: 420 }}>
                  {timePresets.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option === "choose time preset" ? "Custom" : option.replace("last calender", "Last calendar")}
                    </MenuItem>
                  ))}
                </Select>
                {isCustomRange && (
                  <Box sx={{ display: "flex", gap: 1, mt: 1, maxWidth: 420 }}>
                    <TextField
                      size="small"
                      type="date"
                      label="From"
                      fullWidth
                      value={toYmd(report.startDate)}
                      onChange={(e) => setReport({ ...report, startDate: e.target.value || null, preset: "choose time preset" })}
                      error={!!validationErrors.startDate || !!rangeError}
                      helperText={validationErrors.startDate}
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: dayjs().format("YYYY-MM-DD") } }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="To"
                      fullWidth
                      value={toYmd(report.endDate)}
                      onChange={(e) => setReport({ ...report, endDate: e.target.value || null, preset: "choose time preset" })}
                      error={!!validationErrors.endDate || !!rangeError}
                      helperText={validationErrors.endDate}
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: dayjs().format("YYYY-MM-DD") } }}
                    />
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, minHeight: 20 }}>
                  {rangeDays ? `${dayjs(report.startDate).format("MMM D, YYYY")} – ${dayjs(report.endDate).format("MMM D, YYYY")} · ${rangeDays} day${rangeDays === 1 ? "" : "s"} · UTC` : ""}
                </Typography>
                <FieldLabel sx={{ mt: 2 }}>Aggregation period</FieldLabel>
                <Select size="small" fullWidth value={report.timeAggregation} onChange={(e: any) => handleAggregationTimeChange({ target: { value: e.target.value } })} inputProps={{ "aria-label": "Aggregation period" }} sx={{ maxWidth: 420 }}>
                  {timeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FieldLabel sx={{ mt: 2 }}>Time zone</FieldLabel>
                <Select size="small" fullWidth value="UTC" disabled inputProps={{ "aria-label": "Time zone" }} sx={{ maxWidth: 420 }}>
                  <MenuItem value="UTC">(GMT+00:00) UTC</MenuItem>
                </Select>
              </Section>
          </Box>
          <Box sx={{ display: "grid", gap: 3, alignContent: "start", minWidth: 0 }}>
              <Section
                title="Metrics"
                error={validationErrors.metrics}
                action={
                  predictedEventsInResult.length > 0 ? (
                    <MuiTooltip placement="bottom-end" title={forecastTip}>
                      {/* A span, so the tooltip still shows while the control is disabled. */}
                      <span>
                      <FormControlLabel
                        sx={{ mr: 0, pl: 0.5, pr: 1.5, border: 1, borderRadius: "8px", borderColor: forecastOn ? "primary.main" : "divider", bgcolor: forecastOn ? "action.selected" : "transparent", transition: "background-color 0.15s, border-color 0.15s, opacity 0.15s", opacity: rangeHasToday ? 1 : 0.45, cursor: rangeHasToday ? "pointer" : "not-allowed" }}
                        disabled={!rangeHasToday}
                        control={<Checkbox size="small" sx={{ p: 0.5 }} checked={forecastOn} onChange={(e) => setShowForecast(e.target.checked)} />}
                        label={<Typography variant="body2" sx={{ fontWeight: 600, color: rangeHasToday ? "text.primary" : "text.disabled" }}>Forecast</Typography>}
                      />
                      </span>
                    </MuiTooltip>
                  ) : undefined
                }
              >
                <MetricSelect
                  items={metricItems}
                  selected={report.metrics.map((m) => m.eventType)}
                  onChange={handleMetricsChips}
                  locked={lockedMetrics}
                  getDragProps={(index) => getChipDragProps(index, reorderMetrics)}
                />
              </Section>
              <Section title="Dimensions" error={validationErrors.dimensions}>
                <ChipPicker
                  label="Dimensions"
                  items={dimensionItems}
                  selected={report.dimensions.map((d) => d.key)}
                  onChange={(keys) => handleDimensionsChips(splitByCampaign ? [CAMPAIGN_DIMENSION, ...keys] : keys)}
                  showOrder
                  note={dimensionSpeedNote}
                  locked={lockedDimensions}
                  getDragProps={(index) => getChipDragProps(index, reorderDimensions)}
                />
              </Section>
              <Section title="Filters" hint="Applied before grouping." action={report.filters.length > 0 ? <Button size="small" onClick={() => setReport({ ...report, filters: [] })}>Clear all</Button> : undefined}>
              <Filters dimensionsOptions={filterDimensionOptions} filtersData={report.filters} onChange={handleFilterChange} onAddFilter={handleAddFilter} onDeleteFilter={handleDeleteFilter} />
              </Section>
              <Section title="Constraints" hint="Applied after grouping." action={(report.constraints || []).length > 0 ? <Button size="small" onClick={() => setReport({ ...report, constraints: [] })}>Clear all</Button> : undefined}>
              <Constraints metricOptions={constraintMetricOptions} constraintsData={report.constraints || []} onChange={handleConstraintChange} onAddConstraint={handleAddConstraint} onDeleteConstraint={handleDeleteConstraint} />
              </Section>
          </Box>
        </Box>
        </Box>

        {/* Stays in view while scrolling the builder, so Run is never a scroll away. */}
        <Box sx={{ position: "sticky", bottom: 0, zIndex: 2, bgcolor: "background.paper", borderTop: 1, borderColor: "divider", borderRadius: "0 0 12px 12px", px: { xs: 2, md: 3 }, py: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <SpeedPill level={speed.level} label={speed.label} estimate={speed.estimate} />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }} aria-live="polite">
              {reportSummary}
            </Typography>
            {speedReason && (
              <Typography variant="caption" color="text.secondary">
                {speedReason}
              </Typography>
            )}
          </Box>
          {settingsChanged && (
            <Typography variant="caption" color="text.secondary" role="status">
              Settings changed since the last run.
            </Typography>
          )}
          {isFetchingReportData && (
            <Button variant="outlined" onClick={cancel} title="Stop the running query.">
              Cancel
            </Button>
          )}
          <Button variant="contained" size="large" onClick={run} disabled={runDisabled} startIcon={isFetchingReportData ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {isFetchingReportData ? <>Running… <RunTimer /></> : "Run report"}
          </Button>
        </Box>
        </Box>
      </Paper>

      <Paper sx={{ border: 1, borderColor: "divider", boxShadow: "none", borderRadius: "12px", p: { xs: 2, md: 2.5 }, minWidth: 0 }} aria-busy={isFetchingReportData || undefined}>
        <Typography variant="subtitle2" component="h2" sx={{ color: "text.secondary", mb: 1.5 }}>
          Results
        </Typography>
        {isFetchingReportData && !hasResults ? (
          <Box>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1, mb: 1.5 }} />
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={34} />
            ))}
          </Box>
        ) : cancelled && !isFetchingReportData && !hasResults ? (
          <Box role="status" sx={{ py: 6, textAlign: "center" }}>
            <Typography variant="h6">Report cancelled</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              You stopped this run. Nothing was saved or exported.
            </Typography>
            <Button variant="outlined" onClick={run} disabled={runDisabled}>
              Run again
            </Button>
          </Box>
        ) : !isFetchingReportData && reportData?.length === 0 ? (
          reportError ? (
            <MuiAlert severity="error" role="alert" action={<Button color="inherit" size="small" onClick={run} disabled={runDisabled}>Run again</Button>}>
              <AlertTitle>The report didn't finish</AlertTitle>
              {reportError}
            </MuiAlert>
          ) : (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography variant="h6">No rows</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Filters or constraints removed every row, or nothing happened in this range.
              </Typography>
            </Box>
          )
        ) : hasResults ? (
          <>
            <ReportChart
              rows={chartRows ?? []}
              loading={chartLoading}
              report={report}
              columnLabels={new Map(columns.map((c) => [c.name, c.label]))}
              predictionSeries={predictionSeries}
              showForecast={forecastOn}
              predictionError={predictionError}
            />
            <ReportTable data={reportData!} columns={columns} loading={isFetchingReportData} refreshFunc={run} />
          </>
        ) : (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography color="text.secondary">Pick dimensions and metrics, then run the report.</Typography>
          </Box>
        )}
      </Paper>

      <ConfirmDialog
        open={pendingType !== null}
        title={`Switch to a ${pendingType === "advertiser" ? "conversion" : "impression"}-date report?`}
        body="Switching clears your metrics, dimensions and filters."
        confirmLabel="Switch and clear"
        onCancel={() => setPendingType(null)}
        onConfirm={() => {
          if (pendingType) handleReportTypeChange({ target: { value: pendingType } });
          setPendingType(null);
        }}
      />
    </Box>
  );
};

export default ReportBuilderView;
